import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../components/InHouseTooltip/InHouseTooltip";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { SystemReactTinyPopover } from "../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import type { PbjAuditEmployeeRow } from "../../../utils/models";

/** The id every audit sheet / the CMS export shows: the manual override when set, else the synced id. */
export const displayedPbjId = (row: PbjAuditEmployeeRow): string => row.pbjOverrideEmployeeId ?? row.pbjId;

type Props = {
	row: PbjAuditEmployeeRow;
	/** Persists the override (null clears it). Rejects on failure so the editor can stay open for a retry. */
	onSave: (pbjOverrideEmployeeId: string | null) => Promise<unknown>;
	isSaving: boolean;
};

/**
 * DAT-501 Employee ID cell on the audit Employees roster. Shows the effective id
 * (override ?? synced), a "manually set" dot badge with the synced id in its tooltip when an
 * override is active (same idiom as PayTypeOverrideBadge on the Staffing Hours grid), and a
 * pencil revealed on row hover that opens a compact popover editor — a text field prefilled with
 * the current override (empty = revert to the synced id) plus save/cancel.
 */
export const EmployeeIdOverrideCell: FC<Props> = ({ row, onSave, isSaving }) => {
	const { t } = useTranslation();
	const [isEditing, setIsEditing] = useState(false);
	const [draft, setDraft] = useState("");

	const openEditor = () => {
		setDraft(row.pbjOverrideEmployeeId ?? "");
		setIsEditing(true);
	};

	const save = async () => {
		const trimmed = draft.trim();
		try {
			// Empty input clears the override (reverts to the synced id), per the PATCH contract.
			await onSave(trimmed === "" ? null : trimmed);
			setIsEditing(false);
		} catch {
			// Failure is already surfaced by the mutation hook's error snackbar; keep the editor open for a retry.
		}
	};

	const editor = (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: 260 }}>
			<Typography variant="subtitle2">{t("pbj_model.audit.employees.override.edit")}</Typography>
			<SystemValidationTextField
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				placeholder={row.pbjId}
				defaultHelperText={t("pbj_model.audit.employees.override.hint")}
				autoFocus
				fullWidth
				disabled={isSaving}
				onKeyDown={(e) => {
					if (e.key === "Enter") void save();
					if (e.key === "Escape") setIsEditing(false);
				}}
				dataTestId={`pbj-employees-override-input-${row.pbjId}`}
			/>
			<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
				<SystemButton
					buttonVariant="borderless"
					size="small"
					noFullWidthMobile
					onClick={() => setIsEditing(false)}
					data-testid={`pbj-employees-override-cancel-${row.pbjId}`}
				>
					{t("cancel")}
				</SystemButton>
				<SystemButton
					buttonVariant="primary"
					size="small"
					noFullWidthMobile
					loading={isSaving}
					onClick={() => void save()}
					data-testid={`pbj-employees-override-save-${row.pbjId}`}
				>
					{t("save")}
				</SystemButton>
			</Box>
		</Box>
	);

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
			<Box component="span">{displayedPbjId(row) || "—"}</Box>
			{row.pbjOverrideEmployeeId != null && (
				<InHouseTooltip title={t("pbj_model.audit.employees.override.badge_tooltip", { syncedId: row.pbjId })} placement="top" arrow>
					{/* Padded hit area so the tiny dot is actually hoverable — mirrors PayTypeOverrideBadge. */}
					<Box
						component="span"
						role="img"
						aria-label={t("pbj_model.audit.employees.override.badge_tooltip", { syncedId: row.pbjId })}
						data-testid={`pbj-employees-override-badge-${row.pbjId}`}
						sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center", p: 0.5, flexShrink: 0 }}
					>
						<Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "primary.main", display: "block" }} />
					</Box>
				</InHouseTooltip>
			)}
			{/* Rows without a raw synced id (load-only/manual imports) have no override key on the server — no editor. */}
			{Boolean(row.pbjId) && (
				<SystemReactTinyPopover isOpen={isEditing} onClose={() => setIsEditing(false)} positions={["bottom", "top", "right", "left"]} content={editor}>
					<IconButton
						size="small"
						aria-label={t("pbj_model.audit.employees.override.edit")}
						data-testid={`pbj-employees-override-edit-${row.pbjId}`}
						onClick={(e) => {
							e.stopPropagation(); // the row's double-click opens the nurse profile — don't feed it
							openEditor();
						}}
						sx={{
							p: 0.25,
							// Hidden at rest; revealed on row hover (pointer devices) / keyboard focus. Always
							// faintly visible on touch, where there is no hover to reveal it.
							opacity: 0,
							transition: "opacity 0.1s",
							// Both row idioms: the roster renders in a SystemDataGrid, other callers in a plain MUI table.
							".MuiTableRow-root:hover &, .MuiDataGrid-row:hover &": { opacity: 1 },
							"&:focus-visible": { opacity: 1 },
							"@media (hover: none)": { opacity: 0.6 },
						}}
					>
						<EditIcon sx={{ fontSize: 14 }} />
					</IconButton>
				</SystemReactTinyPopover>
			)}
		</Box>
	);
};
